import React, { useEffect, useState, useRef } from "react";
import "../Styling/customerLedger.css";
import { Base_URL } from "../url";
import { useReactToPrint } from "react-to-print";
import { fetchCompnayCodesById, fetchCustomersByOrgId } from "../Api/InvoiceApi";
import { useAuth } from "../Context/AuthContext";

export default function CustomerLedger() {
  const { user } = useAuth();

  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");

  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerCode, setSelectedCustomerCode] = useState("");

  const [ledger, setLedger] = useState({
    customer: {},
    openingBalance: 0,
    currency: "INR",
    transactions: [],
  });

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Customer Ledger",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load dropdowns
  useEffect(() => {
    async function loadData() {
      try {
        if (!user?.organisationId) return;

        const [companies, customers] = await Promise.all([
          fetchCompnayCodesById(user.organisationId),
          fetchCustomersByOrgId(user.organisationId),
        ]);

        setCompanyOptions(companies || []);
        setCustomerOptions(customers || []);
      } catch (e) {
        console.error("Dropdown load failed", e);
      }
    }

    loadData();
  }, [user]);

  // 🔥 Fetch Ledger (UPDATED)
  const fetchLedger = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!selectedCustomerCode) {
        throw new Error("Please select a customer.");
      }

      const url = `${Base_URL}/AdvancePayment/GetCustomerLedger/${user?.organisationId}/${selectedCustomerCode}/${selectedCompanyCode}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Server responded ${res.status}`);

      const data = await res.json();

      const rows = data.res || [];
      const customer = data.customer || {};

      const transactions = rows.map((row) => {
        const isDebit = row.debitcredit === "D";

        return {
          date: row.invoiceDate,
          ref: row.salesInvoiceNo,
          documentName: isDebit ? "Invoice" : "Receipt",
          description: row.referenceNumber || row.docno || "-",
          debit: isDebit ? row.amount : null,
          credit: !isDebit ? row.amount : null,
          balance: row.balance,
        };
      });

      // Opening balance calculation
      let openingBalance = 0;
      if (transactions.length > 0) {
        const first = transactions[0];
        openingBalance =
          first.balance - (first.debit || 0) + (first.credit || 0);
      }

      setLedger({
        customer: {
          customerCode: customer.customerCode || "",
          name: customer.name || "",
          address1: customer.address1 || "",
          address2: customer.address2 || "",
          address3: customer.address3 || "",
          cityName: customer.cityName || "",
          pinCode: customer.pincode || "",
          mobileNumber: customer.mobileNumber || "",
          gstno: customer.gstno || "",
          pan: customer.pan || "",
        },
        openingBalance,
        currency: "INR",
        transactions,
        company:{
          name:data?.company?.name,
          cinNumber:data?.company?.cinNumber,
          logoUrl:data?.company?.logoUrl
        }
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchLedger();
  };

  // Currency formatter
  function Money({ value }) {
    return (
      <span>
        {new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          maximumFractionDigits: 2,
        }).format(value || 0)}
      </span>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Filters */}
        <form onSubmit={handleSubmit} className="flex justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Customer Ledger</h1>
            <p className="text-sm text-gray-600">
              {selectedCustomerCode || "No customer selected"}
            </p>
          </div>

          <div className="flex gap-3 items-center">
            {/* Customer */}
            <select
              value={selectedCustomerCode}
              onChange={(e) => setSelectedCustomerCode(e.target.value)}
              className="px-3 py-2 border rounded"
            >
              <option value="">-- Select Customer --</option>
              {customerOptions.map((c, i) => (
                <option key={i} value={c.customerCode || c.code}>
                  {c.name || c.customerCode}
                </option>
              ))}
            </select>

            {/* Company */}
            <select
              value={selectedCompanyCode}
              onChange={(e) => setSelectedCompanyCode(e.target.value)}
              className="px-3 py-2 border rounded"
            >
              <option value="">-- Select Company --</option>
              {companyOptions.map((c, i) => (
                <option key={i} value={c.companyCode || c.code}>
                  {c.companyName || c.companyCode}
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded"
              disabled={loading}
            >
              {loading ? "Loading..." : "Get Ledger"}
            </button>

            <button
              type="button"
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Download PDF
            </button>
          </div>
        </form>

        {/* Ledger */}
        <div ref={componentRef} className="bg-white p-6 rounded shadow">
          {/* Header */}
<div className="border-b pb-4 mb-4 p-4 rounded-lg">
  {/* TOP: Company + Logo */}
  <div className="flex justify-between items-center mb-3">
    <div>
      <h2 className="text-xl font-bold">
        {ledger?.company?.name || "Company Name"}
      </h2>

      {ledger?.company?.cinNumber && (
        <p className="text-sm text-gray-600">
          CIN: {ledger?.company?.cinNumber}
        </p>
      )}
    </div>

    {ledger?.company?.logoUrl && (
      <img
        src={ledger?.company?.logoUrl}
        alt="Company Logo"
        style={{width:"200px"}}
      />
    )}
  </div>

  {/* TITLE */}
  <div className="text-center mb-3">
    <h1 className="text-2xl font-semibold tracking-wide">
      Statement of Accounts
    </h1>
  </div>

  {/* CUSTOMER + BALANCE */}
  <div className="flex justify-between items-start mt-4">
    {/* LEFT: Customer */}
    <div>
      <h3 className="text-lg font-medium">{ledger.customer.name}</h3>

      <p className="text-sm text-gray-600">
        Code: {ledger.customer.customerCode}
      </p>

      <p className="text-sm text-gray-600">
        {ledger.customer.address1} {ledger.customer.address2}{" "}
        {ledger.customer.address3}
      </p>

      <p className="text-sm text-gray-600">
        {ledger.customer.cityName} - {ledger.customer.pinCode}
      </p>

      <p className="text-sm text-gray-600">
        GST: {ledger.customer.gstno || "-"} | PAN:{" "}
        {ledger.customer.pan || "-"}
      </p>

      <p className="text-sm text-gray-600">
        Mob: {ledger.customer.mobileNumber || "-"}
      </p>
    </div>

    {/* RIGHT: Date + Balance */}
    <div className="text-right">
      <p className="text-sm text-gray-500">
        As on: {new Date().toLocaleDateString()}
      </p>

      <p className="text-lg font-semibold mt-2">Closing Balance</p>

      <p className="text-2xl font-bold">
        <Money
          value={
            ledger.transactions.length
              ? ledger.transactions[
                  ledger.transactions.length - 1
                ].balance
              : ledger.openingBalance
          }
        />
      </p>
    </div>
  </div>
</div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              Opening: <Money value={ledger.openingBalance} />
            </div>
            <div>
              Debit:{" "}
              <Money
                value={ledger.transactions.reduce(
                  (s, t) => s + (t.debit || 0),
                  0
                )}
              />
            </div>
            <div>
              Credit:{" "}
              <Money
                value={ledger.transactions.reduce(
                  (s, t) => s + (t.credit || 0),
                  0
                )}
              />
            </div>
          </div>

          {/* Table */}
          <table className="w-full border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Document No</th>
                <th className="p-2 border">Type</th>
                <th className="p-2 border">Reference</th>
                <th className="p-2 border text-right">Debit</th>
                <th className="p-2 border text-right">Credit</th>
                <th className="p-2 border text-right">Balance</th>
              </tr>
            </thead>

            <tbody>
              {ledger.transactions.map((tx, i) => (
                <tr key={i}>
                  <td className="p-2 border">
                    {new Date(tx.date).toLocaleDateString()}
                  </td>
                  <td className="p-2 border">{tx.ref || "-"}</td>
                  <td className="p-2 border">{tx.documentName}</td>
                  <td className="p-2 border">{tx.description}</td>
                  <td className="p-2 border text-right">
                    {tx.debit && <Money value={tx.debit} />}
                  </td>
                  <td className="p-2 border text-right">
                    {tx.credit && <Money value={tx.credit} />}
                  </td>
                  <td className="p-2 border text-right">
                    <Money value={tx.balance} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      </div>
    </div>
  );
}